import { useEffect, useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import {
  isNetworkAvailable,
  isRecoverableNetworkError,
  queueSetCommentReaction,
} from '@/src/features/offline/outbox-service';
import {
  setCommentReaction,
  subscribeToCommentReaction,
  type CommentReaction,
} from '@/src/features/posts/post-service';
import { trackEvent } from '@/src/lib/analytics';

type CommentReactionState = {
  error: Error | null;
  loading: boolean;
  mutationId: string | null;
  reaction: CommentReaction | null;
};

const COMMENT_REACTION_WRITE_TIMEOUT_MS = 8000;

async function withCommentReactionWriteTimeout<T>(promise: Promise<T>) {
  let timeout: ReturnType<typeof setTimeout> | undefined;

  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timeout = setTimeout(() => {
          reject(new Error('Comment reaction update timed out.'));
        }, COMMENT_REACTION_WRITE_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (timeout) {
      clearTimeout(timeout);
    }
  }
}

export function useCommentReaction({
  commentId,
  postId,
  uid,
}: {
  commentId: string;
  postId: string;
  uid: string | undefined;
}) {
  const [state, setState] = useState<CommentReactionState>({
    error: null,
    loading: Boolean(uid),
    mutationId: null,
    reaction: null,
  });

  useEffect(() => {
    if (!uid) {
      setState({
        error: null,
        loading: false,
        mutationId: null,
        reaction: null,
      });
      return undefined;
    }

    setState((currentState) => ({
      ...currentState,
      error: null,
      loading: true,
    }));

    return subscribeToCommentReaction({
      commentId,
      postId,
      uid,
      onError: (error) => {
        setState((currentState) => ({
          ...currentState,
          error,
          loading: false,
        }));
      },
      onNext: (reaction, mutationId) => {
        setState({ error: null, loading: false, mutationId, reaction });
      },
    });
  }, [commentId, postId, uid]);

  return state;
}

export function useSetCommentReaction(uid: string | undefined) {
  return useMutation({
    mutationFn: async ({
      commentId,
      mutationId,
      postId,
      reaction,
    }: {
      commentId: string;
      mutationId?: string;
      postId: string;
      reaction: CommentReaction | null;
    }): Promise<'queued' | 'saved'> => {
      if (!uid) {
        throw new Error('You must be signed in to react to comments.');
      }

      if (!(await isNetworkAvailable())) {
        await queueSetCommentReaction({
          commentId,
          mutationId,
          postId,
          reaction,
          uid,
        });
        return 'queued';
      }

      try {
        const writePromise = setCommentReaction({
          commentId,
          mutationId,
          postId,
          reaction,
          uid,
        });
        void writePromise.catch(() => undefined);
        await withCommentReactionWriteTimeout(writePromise);
      } catch (error) {
        if (isRecoverableNetworkError(error)) {
          await queueSetCommentReaction({
            commentId,
            mutationId,
            postId,
            reaction,
            uid,
          });
          return 'queued';
        }

        throw error;
      }

      return 'saved';
    },
    onSuccess: (_data, { reaction }) => {
      trackEvent('comment_reaction_set', {
        reaction: reaction ?? 'clear',
      });
    },
  });
}
